'use client';

import { ChangeEvent, FC, FocusEvent, FocusEventHandler, useCallback, useMemo } from 'react';
import { FieldHelperProps, FieldInputProps, useField } from 'formik';
import { ErrorMessage } from 'formik';

import Input, { InputProps } from '@/app/components/Input/Input';

interface FormInputProps extends Omit<InputProps, 'onChange' | 'onBlur' | 'value' | 'isErrorVisible' | 'errorMessage'> {
  onChange?: (value: string | number) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>, field: FieldInputProps<any>, helpers: FieldHelperProps<any>) => void;
}

const FormInput: FC<FormInputProps> = ({ onChange, onBlur, ...props }) => {
  const [field, meta, helpers] = useField(props.name);

  const isErrorVisible = useMemo(() => {
    return !!(meta.error && meta.touched);
  }, [meta.error, meta.touched]);

  const inputType = useMemo(() => {
    // Override number input > we don't want the native number input behavior
    if (props.type === 'number') {
      return 'text';
    }

    return props.type;
  }, [props.type]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (props.type === 'number') {
        // Replace all non digit characters
        const valueSanitized = String(event.target.value).replace(/[\D*]/g, '');

        helpers.setValue(valueSanitized);
        onChange?.(valueSanitized);

        return;
      }

      helpers.setValue(event.target.value);
      onChange?.(event.target.value);
    },
    [helpers, props.type, onChange],
  );

  const handleBlur: FocusEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      field.onBlur(event);
      onBlur?.(event, field, helpers);
    },
    [onBlur, field, helpers],
  );

  return (
    <Input
      {...props}
      type={inputType}
      onChange={handleChange}
      onBlur={handleBlur}
      value={field.value}
      isErrorVisible={isErrorVisible}
      errorMessage={<ErrorMessage name={props.name} />}
    />
  );
};

export default FormInput;
